import { ScopeButton } from '@monorepo/shared-lib';
import { Tooltip } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { MessageStates } from 'containers/MessageBox';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import styled, { createGlobalStyle } from 'styled-components';
import { getCommonLabelFallbackKey } from 'shared/utils/common-label-fallbacks';
import { Preference, PreferenceActions } from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
const CustomDrawerStyle = createGlobalStyle`
  .ant-drawer-body:has(.filter-selection-wrapper) {
    padding-bottom: 0 !important;
  }
`;

const Wrapper = styled.div<{ $compact?: boolean }>`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.$compact ? 'flex-start' : 'space-between')};
  height: ${(props) => (props.$compact ? 'auto' : 'calc(100vh - 130px)')};
  max-height: ${(props) =>
    props.$compact
      ? 'calc(100dvh - 120px)'
      : 'none'};
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  .top-section {
    flex: ${(props) => (props.$compact ? '0 1 auto' : '1')};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .bottom-section {
    flex-shrink: 0;
    padding: ${(props) => (props.$compact ? '8px 0 0' : '16px 0')};
    margin-top: ${(props) => (props.$compact ? '0' : 'auto')};
  }
`;

const FiltersListWrapper = styled.div<{ deduct: number; $compact?: boolean }>`
  max-height: ${(props) =>
    props.$compact
      ? 'min(48vh, 320px)'
      : `calc(100vh - ${props.deduct}px)`};
  min-height: 45px;
  overflow-y: auto;
  background: var(--color-surface-base, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 12px 16px;
  margin-bottom: ${(props) => (props.$compact ? '0' : '15px')};
  ${(props) =>
    props.$compact
      ? `
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 2px;
    align-items: start;
  `
      : ''}
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 12px;
  min-width: 0;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));

  .ant-checkbox-wrapper,
  .ant-checkbox + span {
    color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  }

  .ant-checkbox-wrapper {
    width: 100%;
    min-width: 0;
  }

  .ant-checkbox + span {
    white-space: normal;
    word-break: break-word;
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border, #dbeae3);
  margin-bottom: 8px;
`;

interface FilterSelectionDrawerProps {
  availableFilters: string[];
  selectedFilters: string[];
  onUpdate: (selectedFilters: string[]) => void;
  onClose?: () => void;
  handlePreferences?: (
    action: PreferenceActions,
    preference: Preference
  ) => any;
  currentUser?: { userUuid: string };
  /** When true, drawer does not save preferences (parent saves in onUpdate with section-specific API) */
  skipPreferenceSave?: boolean;
  /** e.g. 'transactions' | 'exceptions' – use t(`${mainLocaleKey}.${filterKey}`) for labels when set */
  mainLocaleKey?: string;
}

const FILTER_SELECTION_PREFERENCE = 'submissions-visible-filters';

// Map filter keys to display names (submissions/main)
const getFilterDisplayName = (
  filterKey: string,
  t: any,
  mainLocaleKey?: string
): string => {
  if (mainLocaleKey) {
    const label = t(`${mainLocaleKey}.${filterKey}`);
    if (label && label !== `${mainLocaleKey}.${filterKey}`) return label;
  }

  const filterMap: Record<string, string> = {
    providerName: t('main.provider'),
    provider: t('main.provider'),
    organisation: t('common.entity'),
    entity: t('common.entity'),
    country: t('common.country'),
    name: t('common.name'),
    description: t('common.description'),
    currency: t('common.currency'),
    nextDueDate: t('main.dueDate'),
    user: t('users.user'),
    status: t('main.status'),
    'dataLog.submissionDate': t('main.submissionDate'),
    submissionDate: t('main.submissionDate'),
    period: t('main.period'),
    mappingName: t('main.taxReturn'),
    selectedValue: t('transactions.mapping'),
    taxName: t('organisation.tax-name'),
    journalCreatedDate: t('vat-report.created-date'),
    accountName: t('transactions.accountName'),
    netAmountConverted: t('organisation.extraction-rules.fields.net'),
    exceptionActionType: t('main.action-required'),
    preparer: t('main.preparer'),
    reviewer: t('main.reviewer'),
    client: t('main.client'),
    submittedBy: t('main.submittedBy'),
  };

  if (filterMap[filterKey]) return filterMap[filterKey];
  const key = filterKey.split('.')[filterKey.split('.').length - 1];
  const commonFallbackKey = getCommonLabelFallbackKey(key);
  if (commonFallbackKey) return t(commonFallbackKey);
  return filterMap[key] || filterKey;
};

export const FilterSelectionDrawer: React.FC<FilterSelectionDrawerProps> = ({
  availableFilters,
  selectedFilters,
  onUpdate,
  onClose,
  handlePreferences,
  currentUser,
  skipPreferenceSave = false,
  mainLocaleKey,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const [localSelectedFilters, setLocalSelectedFilters] =
    useState<string[]>(selectedFilters);

  useEffect(() => {
    setLocalSelectedFilters(selectedFilters);
  }, [selectedFilters]);

  const handleToggleFilter = (filterKey: string) => {
    setLocalSelectedFilters((prev) => {
      if (prev.includes(filterKey)) {
        return prev.filter((f) => f !== filterKey);
      } else {
        return [...prev, filterKey];
      }
    });
  };

  const handleSave = () => {
    if (!skipPreferenceSave && currentUser && handlePreferences) {
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_SELECTION_PREFERENCE,
        value: localSelectedFilters,
      });
    }

    onUpdate(localSelectedFilters);

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-selection-updated'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));

    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <CustomDrawerStyle />
      <Wrapper
        className="filter-selection-wrapper"
        $compact={pwaMobileNav}
      >
        <div className="top-section">
          <ColumnHeader>
            <span>{t('submissions.select-filters')}</span>
            <span>
              {t('submissions.filters-selected-count', {
                count: localSelectedFilters.length,
              })}
            </span>
          </ColumnHeader>
          <FiltersListWrapper
            deduct={200}
            $compact={pwaMobileNav}
            tabIndex={0}
            role="region"
            aria-label={t('main.editFilters')}
          >
            {availableFilters.map((filterKey) => {
              const isSelected = localSelectedFilters.includes(filterKey);
              return (
                <FilterItem key={filterKey}>
                  <Tooltip
                    title={
                      isSelected
                        ? t('common.visible-uncheck-to-hide')
                        : t('common.hidden-check-to-show')
                    }
                  >
                    <ScopeCheckbox
                      checked={isSelected}
                      onChange={() => handleToggleFilter(filterKey)}
                    >
                      {getFilterDisplayName(filterKey, t, mainLocaleKey)}
                    </ScopeCheckbox>
                  </Tooltip>
                </FilterItem>
              );
            })}
          </FiltersListWrapper>
        </div>
        <div className="bottom-section">
          <ScopeButton
            type="primary"
            width="100%"
            height="45px"
            onClick={handleSave}
          >
            {t('common.save')}
          </ScopeButton>
        </div>
      </Wrapper>
    </>
  );
};
